import React, { useEffect, useState, useCallback, useRef } from "react";
import { MDBContainer } from "mdb-react-ui-kit";
import { useAuth } from "../context/AuthContext";
import { resolveBulkUploadProductIds } from "../utils/vintedBulkUpload";
import { setExtensionActiveUser } from "../utils/extensionUserSync";
import "./ViewProductsPage.css";

const BASE_URL =
  "https://clextensions-a6bvf6amfrc9aeex.centralindia-01.azurewebsites.net";

// 🔥 PROFILE_DOMAIN_MAP hardcoded object removed — marketplace ab seedha
// backend se (vinted_stores.marketplace via LEFT JOIN) aati hai, isliye
// ab yahan sirf product ka marketplace field padhna hai.
const getVintedDomain = (product) => {
  return product && product.marketplace ? product.marketplace : "vinted.com";
};

// 🆕 update_details JSON column ko safely read karne ke liye — mysql2
// usually isay already-parsed object bhej deta hai, lekin agar kabhi
// string aa jaye (ya null/undefined) to bhi crash na ho.
const getUpdateDetails = (product) => {
  const raw = product && product.update_details;
  if (!raw) return {};
  if (typeof raw === "object") return raw;
  try {
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
};

const RetryImage = ({ src, alt, className }) => {
  const imgRef = useRef(null);
  const retryCount = useRef(0);
  const MAX_RETRIES = 3;
  const RETRY_DELAY = 2000;

  const handleError = () => {
    if (retryCount.current < MAX_RETRIES) {
      retryCount.current += 1;
      setTimeout(() => {
        if (imgRef.current) {
          imgRef.current.src =
            src + (src.includes("?") ? "&" : "?") + "t=" + Date.now();
        }
      }, RETRY_DELAY * retryCount.current);
    } else {
      if (imgRef.current) {
        imgRef.current.onerror = null;
        imgRef.current.src = "https://via.placeholder.com/120";
      }
    }
  };

  return (
    <img
      ref={imgRef}
      src={src}
      alt={alt}
      className={className}
      loading="lazy"
      onError={handleError}
    />
  );
};

// Pulls the plain numeric value out of a price string like "€24.27" or "24,27 €"
// so it's safe to type into Vinted's price input.
const parsePriceValue = (raw) => {
  if (raw === null || raw === undefined) return "";
  const match = String(raw).match(/\d+([.,]\d+)?/);
  if (!match) return "";
  return match[0].replace(",", ".");
};

const VintedProductsPage = () => {
  const { user, companyId } = useAuth();
// eslint-disable-next-line no-unused-vars
  const userId = user?.id || user?.user_id || user?.userId;

  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [search, setSearch] = useState("");
  const [brand, setBrand] = useState("");
  const [condition, setCondition] = useState("");
  const [size, setSize] = useState("");
  const [status, setStatus] = useState("Active");
  const [storeId, setStoreId] = useState(""); // 🔥 profileUsername -> storeId
  const [uploadDate, setUploadDate] = useState("");
  const [priceMin, setPriceMin] = useState("");
  const [priceMax, setPriceMax] = useState("");
  const [viewsMin, setViewsMin] = useState("");
  const [viewsMax, setViewsMax] = useState("");
  const [favMin, setFavMin] = useState("");
  const [favMax, setFavMax] = useState("");
  // 🆕 filter for items with ANY pending change (price, title, status, ...)
  // — renamed from priceUpdateRequired since it's no longer price-only.
  const [upcomingUpdatesFilter, setUpcomingUpdatesFilter] = useState("");
  const [sort, setSort] = useState("scraped_at");
  const [order, setOrder] = useState("desc");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [limit, setLimit] = useState(20);
  const [stores, setStores] = useState([]); // 🔥 profiles -> stores

  const [selectedProductIds, setSelectedProductIds] = useState([]);
  const [bulkUploading, setBulkUploading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [priceUpdateLoading, setPriceUpdateLoading] = useState(false);
  const [updatingAllDates, setUpdatingAllDates] = useState(false);

  // 🆕 Set Price modal state
  const [priceModalItem, setPriceModalItem] = useState(null); // the product row (has .id, .title, .price, .update_details)
  const [priceModalValue, setPriceModalValue] = useState("");
  const [priceModalSaving, setPriceModalSaving] = useState(false);

  // Rows seen so far, keyed by Vinted listing id — selection is kept across
  // pages, so Bulk Upload needs rows that are no longer on the current page.
  const loadedRowsRef = useRef({});

  const [sellerMap, setSellerMap] = useState({});
  const [sellerFilter, setSellerFilter] = useState("");
  const [allSellers, setAllSellers] = useState([]); // 🔥 saare sellers

  // 1. Fetch stores dropdown (replaces the old vinted-profiles fetch)
  useEffect(() => {
    fetch(`${BASE_URL}/api/vinted-store-list`)
      .then((res) => res.json())
      .then((data) => {
        if (data.success) setStores(data.stores);
      })
      .catch((err) => console.error("Failed to fetch stores", err));
  }, []);

  // 2. 🔥 Fetch ALL distinct sellers on mount
  useEffect(() => {
    fetch(`${BASE_URL}/api/vinted-sellers`)
      .then(res => res.json())
      .then(data => {
        if (data.success) setAllSellers(data.sellers);
      })
      .catch(err => console.error("Failed to fetch sellers:", err));
  }, []);

  // 3. Seller map for current page's products (badges ke liye) — ab Vinted product_id se match hota hai, title se nahi
  const fetchSellerMap = useCallback(async (productList) => {
    const uniqueProductIds = [
      ...new Set(
        productList.map((p) => p.product_id).filter(Boolean)
      ),
    ];
    if (uniqueProductIds.length === 0) {
      setSellerMap({});
      return;
    }
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`${BASE_URL}/api/products-seller-map-by-vinted-id`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ productIds: uniqueProductIds }),
      });
      const data = await res.json();
      if (data.success) {
        setSellerMap(data.data);
      }
    } catch (err) {
      console.error("Failed to fetch seller map:", err);
    }
  }, []);

  // 4. 🔥 fetchProducts – ab store_id bhi bhejo (server-side filter)
  const fetchProducts = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page,
        limit,
        sort,
        order,
        search,
        brand,
        condition,
        size,
        status,
        store_id: storeId, // 🔥 profile_username -> store_id
        upload_date: uploadDate,
        price_min: priceMin,
        price_max: priceMax,
        views_min: viewsMin,
        views_max: viewsMax,
        favourites_min: favMin,
        favourites_max: favMax,
      });

      if (sellerFilter) {
        params.append("seller_name", sellerFilter);
      }

      // 🆕 only sent when the "Upcoming Updates" filter is set — server
      // param renamed price_update_required -> upcoming_updates
      if (upcomingUpdatesFilter) {
        params.append("upcoming_updates", upcomingUpdatesFilter);
      }

      const res = await fetch(`${BASE_URL}/api/vinted-products?${params}`);
      const data = await res.json();
      if (data.success) {
        data.data.forEach((row) => {
          loadedRowsRef.current[String(row.product_id)] = row;
        });
        setProducts(data.data);
        setTotalPages(data.totalPages);
        fetchSellerMap(data.data);
      } else {
        setError(data.error);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [
    page, limit, sort, order, search, brand, condition, size, status,
    storeId, uploadDate, priceMin, priceMax, viewsMin, viewsMax,
    favMin, favMax, sellerFilter, upcomingUpdatesFilter, fetchSellerMap,
  ]);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  const getSellerInfo = (product) => {
    const key = String(product.product_id);
    return sellerMap[key] || null;
  };

  // Makes the extension in this browser act as companyId before a bulk action.
  // Returns false (after alerting) if the extension could not switch.
  const syncExtensionUser = async () => {
    console.log(`[USER-ID-DEBUG][FRONTEND] selected userId (companyId)=${companyId}; switching extension`);
    const result = await setExtensionActiveUser(companyId);

    if (result.via === "extension") {
      if (result.success) {
        console.log(`[USER-ID-DEBUG][FRONTEND] extension confirmed active userId=${result.userId}`);
        return true;
      }
      console.error(`[USER-ID-DEBUG][FRONTEND] extension failed to switch to userId=${companyId}:`, result.error);
      alert(`The Chrome extension could not switch to user ${companyId}: ${result.error}. Nothing was sent.`);
      return false;
    }

    // Older extension without the page bridge: legacy server-side sync.
    console.warn(`[USER-ID-DEBUG][FRONTEND] no extension bridge found; using legacy /api/set-user-id for userId=${companyId}`);
    await fetch(`${BASE_URL}/api/set-user-id`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ userId: companyId }),
    });
    await new Promise((resolve) => setTimeout(resolve, 2000));
    return true;
  };

  // Bulk actions (unchanged)
  const handleBulkDeleteVinted = async () => {
    if (selectedProductIds.length === 0) return;
    if (!companyId) return;
    if (!window.confirm(`Delete ${selectedProductIds.length} selected items?`)) return;

    setDeleteLoading(true);
    try {
      if (!(await syncExtensionUser())) return;

      const res = await fetch(`${BASE_URL}/api/bulk-delete-vinted`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ userId: companyId, productIds: selectedProductIds }),
      });
      const data = await res.json();
      if (data.success) {
        alert(`Deleted: ${data.message}`);
        setSelectedProductIds([]);
        fetchProducts();
      } else {
        alert("Delete failed: " + data.error);
      }
    } catch (err) {
      alert("Network error: " + err.message);
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleUpdateAllUploadDates = async () => {
    if (!companyId) return;
    if (!window.confirm("This will re-scrape upload dates for ALL products. Continue?")) return;
    setUpdatingAllDates(true);
    try {
      const res = await fetch(`${BASE_URL}/api/update-all-upload-dates`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ userId: companyId }),
      });
      const data = await res.json();
      if (data.success) {
        alert(`Upload dates updated. Updated: ${data.updated}, Skipped: ${data.skipped}`);
        fetchProducts();
      } else {
        alert("Update failed: " + (data.message || data.error || "Unknown error"));
      }
    } catch (err) {
      alert("Network error: " + err.message);
    } finally {
      setUpdatingAllDates(false);
    }
  };

  const handleBulkUploadVinted = async () => {
    if (selectedProductIds.length === 0) return;
    if (!companyId) return;

    // Selection holds Vinted listing ids (Bulk Delete needs those); the upload
    // endpoint needs our internal products.id, same as View Products sends.
    const selectedRows = selectedProductIds.map(
      (vintedId) =>
        loadedRowsRef.current[String(vintedId)] || { product_id: vintedId, internal_product_id: null }
    );
    const { productIds, skipped } = resolveBulkUploadProductIds(selectedRows);

    const skippedSummary = skipped
      .slice(0, 10)
      .map((s) => `• ${s.title || s.vintedId}: ${s.reason}`)
      .join("\n");
    const moreSkipped = skipped.length > 10 ? `\n…and ${skipped.length - 10} more` : "";

    if (productIds.length === 0) {
      alert(`No selected products can be uploaded.\n\n${skippedSummary}${moreSkipped}`);
      return;
    }
    if (
      !window.confirm(
        `Upload ${productIds.length} product(s) to Vinted as new listings?` +
          (skipped.length
            ? `\n\n${skipped.length} selected row(s) will be skipped:\n${skippedSummary}${moreSkipped}`
            : "")
      )
    ) {
      return;
    }

    setBulkUploading(true);
    try {
      if (!(await syncExtensionUser())) return;

      console.log(`[USER-ID-DEBUG][FRONTEND] upload initiated: POST /api/bulk-upload-vinted userId=${companyId}, products=${productIds.length}`);
      const res = await fetch(`${BASE_URL}/api/bulk-upload-vinted`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ userId: companyId, productIds }),
      });
      const data = await res.json();
      if (data.success) {
        const succeeded = data.results?.filter((r) => r.success) || [];
        const failed = data.results?.filter((r) => !r.success) || [];
        if (failed.length > 0) {
          console.error("❌ Vinted bulk upload — failed products:", failed);
          alert(
            `⚠️ Vinted upload: ${succeeded.length} queued, ${failed.length} FAILED.\n\n` +
              `First failure: Product ${failed[0].productId} — ${failed[0].error}`
          );
        } else {
          alert(`✅ Vinted bulk upload started! ${succeeded.length} products queued.`);
        }
        setSelectedProductIds([]);
      } else {
        alert("Bulk upload failed: " + data.error);
      }
    } catch (err) {
      alert("Network error: " + err.message);
    } finally {
      setBulkUploading(false);
    }
  };

   const handleBulkPriceUpdateVinted = async () => {
    if (selectedProductIds.length === 0) return;
    if (!companyId) return;

    // 🆕 Ab price OR title — dono mein se koi bhi pending ho to item eligible hai push ke liye,
    // kyunki update_details ab dono field store kar sakta hai.
    const pendingItems = products.filter((p) => {
      if (!selectedProductIds.includes(p.product_id)) return false;
      const details = getUpdateDetails(p);
      const hasPendingPrice = details.price !== undefined && details.price !== null;
      const hasPendingTitle = details.title !== undefined && details.title !== null && details.title !== "";
      return Number(p.upcoming_updates) === 1 && (hasPendingPrice || hasPendingTitle);
    });

    if (pendingItems.length === 0) {
      alert("None of the selected products have a pending update. Use 'Update Price' first to set a new price or title.");
      return;
    }

    if (
      !window.confirm(
        `Push pending changes to Vinted for ${pendingItems.length} selected item(s)? This will open each listing, edit it, and save the changes.`
      )
    )
      return;

    setPriceUpdateLoading(true);
    try {
      if (!(await syncExtensionUser())) return;

      const res = await fetch(`${BASE_URL}/api/bulk-price-update-vinted`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          userId: companyId,
          productIds: pendingItems.map((p) => p.product_id),
        }),
      });
      const data = await res.json();
      if (data.success) {
        alert(`Price update started for ${pendingItems.length} product(s).`);
        setSelectedProductIds([]);
        fetchProducts(); // 🔥 refresh so "Price Pending" badge clears
      } else {
        alert("Price update failed: " + (data.error || "Unknown error"));
      }
    } catch (err) {
      alert("Network error: " + err.message);
    } finally {
      setPriceUpdateLoading(false);
    }
  };

  // 🆕 ── Set Price modal handlers ─────────────────────────────────────────
  // Opens the modal for a single row. Pre-fills with any already-pending
  // update_details.price value, otherwise falls back to the current price.
  const openPriceModal = (product) => {
    setPriceModalItem(product);
    const details = getUpdateDetails(product);
    const prefill =
      details.price !== undefined && details.price !== null
        ? parsePriceValue(details.price)
        : parsePriceValue(product.price);
    setPriceModalValue(prefill);
  };

  const closePriceModal = () => {
    setPriceModalItem(null);
    setPriceModalValue("");
    setPriceModalSaving(false);
  };

  const handleSavePriceModal = async () => {
    if (!priceModalItem) return;

    const numericPrice = Number(priceModalValue);
    if (!priceModalValue || Number.isNaN(numericPrice) || numericPrice <= 0) {
      alert("Please enter a valid price greater than 0.");
      return;
    }

    setPriceModalSaving(true);
    try {
      const res = await fetch(
        `${BASE_URL}/api/v1/vinted-listed-items/${priceModalItem.id}/update-price`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ price: numericPrice }),
        }
      );
      const data = await res.json();

      if (data.success) {
        // Reflect the change locally right away so the badge/filter updates
        // without waiting for a full refetch. Merge into update_details
        // rather than overwrite it, so any other pending field survives.
        setProducts((prev) =>
          prev.map((p) =>
            p.id === priceModalItem.id
              ? {
                  ...p,
                  update_details: { ...getUpdateDetails(p), price: numericPrice },
                  upcoming_updates: 1,
                }
              : p
          )
        );
        closePriceModal();
      } else {
        alert("Failed to save price: " + (data.message || "Unknown error"));
        setPriceModalSaving(false);
      }
    } catch (err) {
      alert("Network error: " + err.message);
      setPriceModalSaving(false);
    }
  };
  // ─────────────────────────────────────────────────────────────────────

  const handleSelectProduct = (productId) => {
    setSelectedProductIds((prev) =>
      prev.includes(productId)
        ? prev.filter((id) => id !== productId)
        : [...prev, productId]
    );
  };

  const handleSelectAll = () => {
    if (products.length === 0) return;
    const allIds = products.map((p) => p.product_id);
    const allSelected = allIds.every((id) => selectedProductIds.includes(id));
    if (allSelected) {
      setSelectedProductIds((prev) => prev.filter((id) => !allIds.includes(id)));
    } else {
      setSelectedProductIds((prev) => [...new Set([...prev, ...allIds])]);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    fetchProducts();
  };

  const handleClearFilters = () => {
    setSearch("");
    setBrand("");
    setCondition("");
    setSize("");
    setStatus("");
    setStoreId(""); // 🔥 profileUsername -> storeId
    setUploadDate("");
    setPriceMin("");
    setPriceMax("");
    setViewsMin("");
    setViewsMax("");
    setFavMin("");
    setFavMax("");
    setUpcomingUpdatesFilter("");
    setSort("scraped_at");
    setOrder("desc");
    setPage(1);
    setSellerFilter("");
  };

  return (
    <MDBContainer fluid className="view-products-container">
      <h2 className="mb-4">Vinted Products</h2>

      <div className="mb-3">
        <button
          type="button"
          className="btn btn-warning"
          onClick={handleUpdateAllUploadDates}
          disabled={updatingAllDates}
        >
          {updatingAllDates ? "Updating..." : "Refresh All Missing Upload Dates"}
        </button>
        <small className="text-muted ms-2">
          Updates upload dates for products that do not have one yet.
        </small>
      </div>

      {selectedProductIds.length > 0 && (
        <div className="selection-bar">
          <span className="selection-count">
            <strong>{selectedProductIds.length}</strong> product(s) selected
          </span>
          <button
            type="button"
            className="btn btn-link btn-sm"
            onClick={() => setSelectedProductIds([])}
          >
            Clear Selection
          </button>
          <button
            type="button"
            className="btn btn-info btn-sm ms-2"
            onClick={handleBulkUploadVinted}
            disabled={bulkUploading}
          >
            {bulkUploading
              ? "Uploading..."
              : `Bulk Upload to Vinted (${selectedProductIds.length})`}
          </button>
          <button
            type="button"
            className="btn btn-success btn-sm ms-2"
            onClick={handleBulkPriceUpdateVinted}
            disabled={priceUpdateLoading}
            title="Push each selected product's table price into its live Vinted listing"
          >
            {priceUpdateLoading
              ? "Updating Prices..."
              : `Bulk Price Update (${selectedProductIds.length})`}
          </button>
          <button
            type="button"
            className="btn btn-danger btn-sm ms-2"
            onClick={handleBulkDeleteVinted}
            disabled={deleteLoading}
          >
            {deleteLoading ? "Deleting..." : `Bulk Delete (${selectedProductIds.length})`}
          </button>
        </div>
      )}

      <div className="filters-section">
        <form onSubmit={handleSearch} className="filters-grid">
          <div className="filter-item">
            <label>Search</label>
            <input
              className="filter-input"
              placeholder="Title, Brand, ID..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>

          <div className="filter-item">
            <label>Brand</label>
            <input
              className="filter-input"
              placeholder="e.g. Nike"
              value={brand}
              onChange={(e) => setBrand(e.target.value)}
            />
          </div>

          <div className="filter-item">
            <label>Condition</label>
            <select
              className="filter-select"
              value={condition}
              onChange={(e) => setCondition(e.target.value)}
            >
              <option value="">All</option>
              <option value="New with tags">New with tags</option>
              <option value="Very good">Very good</option>
              <option value="Good">Good</option>
              <option value="Satisfactory">Satisfactory</option>
            </select>
          </div>

          <div className="filter-item">
            <label>Size</label>
            <input
              className="filter-input"
              placeholder="e.g. L, 42"
              value={size}
              onChange={(e) => setSize(e.target.value)}
            />
          </div>

          <div className="filter-item">
            <label>Status</label>
            <select
              className="filter-select"
              value={status}
              onChange={(e) => setStatus(e.target.value)}
            >
              <option value="">All</option>
              <option value="Active">Active</option>
              <option value="Sold">Sold</option>
              <option value="Draft">Draft</option>
            </select>
          </div>

          {/* 🔥 STORE DROPDOWN – ab profile_username ki jagah store_id se filter */}
          <div className="filter-item">
            <label>Store</label>
            <select
              className="filter-select"
              value={storeId}
              onChange={(e) => setStoreId(e.target.value)}
            >
              <option value="">All Stores</option>
              {stores.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.store_name || s.username}
                </option>
              ))}
            </select>
          </div>

          {/* 🔥 SELLER DROPDOWN – ab allSellers se populate */}
          <div className="filter-item">
            <label>Seller (ERP)</label>
            <select
              className="filter-select"
              value={sellerFilter}
              onChange={(e) => {
                setSellerFilter(e.target.value);
                setPage(1); // reset page
              }}
            >
              <option value="">All Sellers</option>
              {allSellers.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </div>

          {/* 🆕 Upcoming Updates filter — generic now (price, title, status...) */}
          <div className="filter-item">
            <label>Upcoming Updates</label>
            <select
              className="filter-select"
              value={upcomingUpdatesFilter}
              onChange={(e) => {
                setUpcomingUpdatesFilter(e.target.value);
                setPage(1);
              }}
            >
              <option value="">All</option>
              <option value="1">Yes (has pending changes)</option>
              <option value="0">No</option>
            </select>
          </div>

          <div className="filter-item">
            <label>Upload Date</label>
            <input
              className="filter-input"
              placeholder="e.g. 2 days ago, a week ago"
              value={uploadDate}
              onChange={(e) => setUploadDate(e.target.value)}
            />
          </div>

          <div className="filter-item">
            <label>Min Price</label>
            <input
              type="number"
              className="filter-input"
              placeholder="0"
              value={priceMin}
              onChange={(e) => setPriceMin(e.target.value)}
            />
          </div>

          <div className="filter-item">
            <label>Max Price</label>
            <input
              type="number"
              className="filter-input"
              placeholder="999"
              value={priceMax}
              onChange={(e) => setPriceMax(e.target.value)}
            />
          </div>

          <div className="filter-item">
            <label>Min Views</label>
            <input
              type="number"
              className="filter-input"
              placeholder="0"
              value={viewsMin}
              onChange={(e) => setViewsMin(e.target.value)}
            />
          </div>

          <div className="filter-item">
            <label>Max Views</label>
            <input
              type="number"
              className="filter-input"
              placeholder="9999"
              value={viewsMax}
              onChange={(e) => setViewsMax(e.target.value)}
            />
          </div>

          <div className="filter-item">
            <label>Min Favorites</label>
            <input
              type="number"
              className="filter-input"
              placeholder="0"
              value={favMin}
              onChange={(e) => setFavMin(e.target.value)}
            />
          </div>

          <div className="filter-item">
            <label>Max Favorites</label>
            <input
              type="number"
              className="filter-input"
              placeholder="9999"
              value={favMax}
              onChange={(e) => setFavMax(e.target.value)}
            />
          </div>

          <div className="filter-item">
            <label>Sort by</label>
            <select
              className="filter-select"
              value={sort}
              onChange={(e) => setSort(e.target.value)}
            >
              <option value="scraped_at">Last Scraped</option>
              <option value="price">Price</option>
              <option value="views">Views</option>
              <option value="favourites">Favorites</option>
              <option value="upload_date">Upload Date</option>
            </select>
          </div>

          <div className="filter-item">
            <label>Order</label>
            <select
              className="filter-select"
              value={order}
              onChange={(e) => setOrder(e.target.value)}
            >
              <option value="desc">Descending</option>
              <option value="asc">Ascending</option>
            </select>
          </div>

          <div className="filter-item">
            <label>Items per page</label>
            <select
              className="filter-select"
              value={limit}
              onChange={(e) => {
                setLimit(parseInt(e.target.value, 10));
                setPage(1);
              }}
            >
              <option value={20}>20 per page</option>
              <option value={50}>50 per page</option>
              <option value={100}>100 per page</option>
            </select>
          </div>

          <div className="filter-item" style={{ alignSelf: "flex-end" }}>
            <button type="submit" className="btn btn-outline-primary me-2">
              Search
            </button>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={handleClearFilters}
            >
              Clear
            </button>
          </div>
        </form>
      </div>

      {error && <div className="alert alert-danger">{error}</div>}

      {sellerFilter && (
        <div className="active-filters mb-2">
          <small className="text-muted">
            Filtered by seller: <strong>{sellerFilter}</strong>
          </small>
        </div>
      )}

      {loading ? (
        <div className="py-5 text-center">
          <h4>Loading Vinted products...</h4>
        </div>
      ) : (
        <>
          {products.length > 0 && (
            <div className="select-all-row">
              <input
                type="checkbox"
                id="selectAll"
                checked={
                  products.length > 0 &&
                  products.every((p) =>
                    selectedProductIds.includes(p.product_id)
                  )
                }
                onChange={handleSelectAll}
              />
              <label htmlFor="selectAll">Select All (current page)</label>
            </div>
          )}

          <div className="product-list">
            {products.length === 0 ? (
              <p className="text-center">No products found.</p>
            ) : (
              products.map((p) => {
                const sellerInfo = getSellerInfo(p);
                const sellerName =
                  sellerInfo && sellerInfo.seller_name
                    ? sellerInfo.seller_name
                    : "Unknown Seller";
                const isAmbiguous = Boolean(sellerInfo && sellerInfo.ambiguous);
                const vintedDomain = getVintedDomain(p); // 🔥 ab pura product object pass hota hai

                            // 🆕 update_details se saari pending fields nikaalo (price, title, ya koi bhi future field)
                const updateDetails = getUpdateDetails(p);
                const pendingPrice = updateDetails.price;
                const pendingTitle = updateDetails.title;

                const hasPendingPrice = pendingPrice !== undefined && pendingPrice !== null;
                const hasPendingTitle = pendingTitle !== undefined && pendingTitle !== null && pendingTitle !== "";

                const needsPriceUpdate =
                  Number(p.upcoming_updates) === 1 && hasPendingPrice;

                // 🆕 Generic list of "known" pending fields already shown as their own badge
                // (price aur title alag se handle ho rahe hain neeche) — agar update_details
                // mein koi aisi field ho jo in dono mein se na ho, wo yahan se pick ho jayegi
                // taake future mein naye fields (jaise 'condition', 'size') bhi automatically dikhein.
                const knownFields = new Set(["price", "title"]);
                const otherPendingFields = Object.entries(updateDetails).filter(
                  ([key, val]) =>
                    !knownFields.has(key) && val !== undefined && val !== null && val !== ""
                );

                return (
                  <div
                    key={p.id}
                    className={`product-card ${
                      selectedProductIds.includes(p.product_id) ? "selected" : ""
                    }`}
                  >
                    <div className="product-main">
                      <div className="product-main-content">
                        <div className="product-checkbox">
                          <input
                            type="checkbox"
                            checked={selectedProductIds.includes(p.product_id)}
                            onChange={() => handleSelectProduct(p.product_id)}
                          />
                        </div>

                        <RetryImage
                          src={p.image_url || "https://via.placeholder.com/120"}
                          alt={p.title}
                          className="product-image"
                        />

                        <div className="product-info">
                          <h5 className="product-title">{p.title}</h5>

                                                <div className="product-badges">
                            <span className="product-badge">Price: {p.price}</span>
                            {needsPriceUpdate && (
                              <span
                                className="product-badge bg-warning"
                                title="This item's price hasn't been pushed to Vinted yet"
                              >
                                New price pending: {pendingPrice}
                              </span>
                            )}
                            {hasPendingTitle && (
                              <span
                                className="product-badge bg-warning"
                                title={`This item's title hasn't been pushed yet. Current: "${p.title}"`}
                              >
                                New title pending: {pendingTitle}
                              </span>
                            )}
                            {otherPendingFields.map(([key, val]) => (
                              <span
                                key={key}
                                className="product-badge bg-warning"
                                title={`This item's ${key} hasn't been pushed yet`}
                              >
                                New {key} pending: {String(val)}
                              </span>
                            ))}
                            <span className="product-badge">
                              Brand: {p.brand || "N/A"}
                            </span>
                            <span className="product-badge">
                              Condition: {p.condition_status}
                            </span>
                            <span className="product-badge">
                              Size: {p.size || "N/A"}
                            </span>
                            <span className="product-badge">Views: {p.views}</span>
                            <span className="product-badge">
                              Favorites: {p.favourites}
                            </span>
                            <span className="product-badge">
                              Upload Date: {p.upload_date || "N/A"}
                            </span>
                            <span
                              className={`product-badge ${
                                p.status === "Sold"
                                  ? "bg-danger"
                                  : p.status === "Draft"
                                  ? "bg-warning"
                                  : "bg-success"
                              }`}
                            >
                              {p.status || "Active"}
                            </span>
                          </div>

                          <div className="marketplace-badges">
                            <span
                              className="product-badge"
                              title={
                                isAmbiguous
                                  ? "Title ERP mein 2 ya zyada baar mili hai, manually verify karo"
                                  : ""
                              }
                              style={
                                isAmbiguous
                                  ? { border: "1px solid #dc3545" }
                                  : undefined
                              }
                            >
                              Seller: {sellerName}
                              {isAmbiguous ? " (check)" : ""}
                            </span>
                            <span className="product-badge">
                              Profile: {p.profile_username || p.store_name || "N/A"}
                            </span>
                            <a
                              href={`https://www.${vintedDomain}/items/${p.product_id}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="btn btn-sm btn-info ms-2"
                            >
                              View on Vinted
                            </a>
                            {/* 🆕 Update Price button */}
                            <button
                              type="button"
                              className={`btn btn-sm ms-2 ${
                                needsPriceUpdate ? "btn-warning" : "btn-outline-success"
                              }`}
                              onClick={() => openPriceModal(p)}
                              title="Set a new price for this Vinted listing"
                            >
                              {needsPriceUpdate ? "Price Pending" : "Update Price"}
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>

          {totalPages > 1 && (
            <div className="pagination mt-3 justify-content-center">
              <button
                className="btn btn-outline-primary me-2"
                disabled={page === 1}
                onClick={() => setPage(page - 1)}
              >
                Previous
              </button>
              <span className="align-self-center">
                Page {page} of {totalPages}
              </span>
              <button
                className="btn btn-outline-primary ms-2"
                disabled={page === totalPages}
                onClick={() => setPage(page + 1)}
              >
                Next
              </button>
            </div>
          )}
        </>
      )}

      {/* 🆕 Set Price modal — styled the same way as the Edit Images modal */}
      {priceModalItem && (
        <div className="reason-modal-overlay" onClick={closePriceModal}>
          <div
            className="reason-modal-content"
            onClick={(e) => e.stopPropagation()}
            style={{ maxWidth: 420 }}
          >
            <div className="modal-header">
              <div className="modal-title-section">
                <h2 className="modal-product-title">Update Price</h2>
                <h3 className="modal-analysis-type">
                  {priceModalItem.title}
                </h3>
              </div>
            </div>
            <div className="modal-body">
              <label
                htmlFor="set-price-input"
                style={{ display: "block", marginBottom: 6, fontWeight: 600 }}
              >
                Set Price
              </label>
              <input
                id="set-price-input"
                type="number"
                step="0.01"
                min="0"
                className="filter-input"
                style={{ width: "100%" }}
                value={priceModalValue}
                onChange={(e) => setPriceModalValue(e.target.value)}
                placeholder="e.g. 45.00"
                autoFocus
              />
              <small className="text-muted d-block mt-2">
                Current price: {priceModalItem.price}. This won't change the
                price shown above right away — it just marks this item as
                needing a price update on Vinted.
              </small>
            </div>
            <div className="modal-footer">
              <button
                className="modal-btn-close modal-btn-secondary"
                onClick={closePriceModal}
                disabled={priceModalSaving}
              >
                Cancel
              </button>
              <button
                className="modal-btn-close"
                onClick={handleSavePriceModal}
                disabled={priceModalSaving || !priceModalValue}
              >
                {priceModalSaving ? "Saving..." : "Save Price"}
              </button>
            </div>
          </div>
        </div>
      )}
    </MDBContainer>
  );
};

export default VintedProductsPage;